import React, { useRef, useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PenTool, Type, Eraser, Check, ChevronLeft, ChevronRight, Loader2, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { ZoomBar, usePinchZoom } from './signature/ZoomControls';
import { SIGNATURE_STYLES, generateTypedSignature, SignatureCanvas } from './signature/SignatureCanvas';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const formatSignDateTime = () => {
  const now = new Date();
  return now.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

/* ============================================================
   STEP 1 — Create Signature (Draw or Type)
   ============================================================ */
const StepSignature = ({ signatureImage, setSignatureImage, signerName, setSignerName }) => {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('classic');
  const [hasDrawn, setHasDrawn] = useState(false);

  const captureDrawn = () => {
    const pad = canvasRef.current?._pad?.current;
    if (pad && !pad.isEmpty()) setSignatureImage(pad.toDataURL('image/png'));
  };

  const clearSig = () => {
    const pad = canvasRef.current?._pad?.current;
    if (pad) pad.clear();
    setHasDrawn(false);
    setSignatureImage(null);
    setTypedName('');
  };

  useEffect(() => {
    if (mode === 'type' && typedName.trim()) {
      setSignatureImage(generateTypedSignature(typedName, selectedStyle));
    }
  }, [typedName, selectedStyle, mode, setSignatureImage]);

  return (
    <div className="space-y-4" data-testid="step-signature">
      <p className="text-white/50 text-sm">Create your signature by drawing or typing your name.</p>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button onClick={() => { setMode('draw'); setSignatureImage(null); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-medium transition-all border ${mode === 'draw' ? 'bg-primary text-black border-primary' : 'border-white/10 text-white/60 hover:text-white hover:border-white/20'}`}
          data-testid="sig-mode-draw">
          <PenTool className="w-4 h-4" /> Draw
        </button>
        <button onClick={() => { setMode('type'); setSignatureImage(null); clearSig(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-medium transition-all border ${mode === 'type' ? 'bg-primary text-black border-primary' : 'border-white/10 text-white/60 hover:text-white hover:border-white/20'}`}
          data-testid="sig-mode-type">
          <Type className="w-4 h-4" /> Type
        </button>
      </div>

      {mode === 'draw' ? (
        <div className="space-y-3">
          <SignatureCanvas canvasRef={canvasRef} onDrawEnd={() => { setHasDrawn(true); setTimeout(captureDrawn, 50); }} height={160} />
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={clearSig} className="border-white/10 text-white/60 hover:bg-white/5" data-testid="clear-signature-btn">
              <Eraser className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
            {hasDrawn && !signatureImage && (
              <Button type="button" size="sm" onClick={captureDrawn} className="bg-primary text-black" data-testid="capture-signature-btn">
                <Check className="w-3.5 h-3.5 mr-1.5" /> Capture
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name"
            className="bg-black/50 border-white/10 text-white h-11 text-base" data-testid="sig-type-input" autoFocus />
          {typedName.trim() && (
            <div className="grid grid-cols-1 gap-1.5 max-h-44 overflow-y-auto">
              {SIGNATURE_STYLES.map(style => (
                <button key={style.id} onClick={() => setSelectedStyle(style.id)}
                  className={`border rounded-sm p-3 bg-white text-center transition-all ${selectedStyle === style.id ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`sig-style-${style.id}`}>
                  <span style={{ font: style.font, color: style.color }}>{typedName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Signature preview */}
      {signatureImage && (
        <div className="border border-primary/30 rounded-sm bg-white p-3 text-center" data-testid="signature-preview">
          <img src={signatureImage} alt="Your signature" className="h-14 mx-auto object-contain" />
          <p className="text-green-600 text-xs mt-1.5 flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Signature captured</p>
        </div>
      )}

      {/* Full name input */}
      <div className="space-y-1.5">
        <label className="text-white/60 text-xs font-medium">Full Legal Name</label>
        <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Enter your full name"
          className="bg-black/50 border-white/10 text-white h-10" data-testid="signer-name-input" />
      </div>
    </div>
  );
};

/* ============================================================
   STEP 2 — Fill Fields (if template has them)
   ============================================================ */
const StepFields = ({ fields, filledFields, setFilledFields, signatureImage, signerName }) => {
  const signDateTime = formatSignDateTime();

  // Auto-fill on mount
  useEffect(() => {
  const autoFills = {};

  fields.forEach(f => {
    if (f.type === 'date' && !filledFields[f.field_id]?.filled) {
      autoFills[f.field_id] = { 
        type: 'date', 
        data: signDateTime, 
        position: f.position, 
        page: f.page || 1, 
        filled: true 
      };
    }
    if (f.type === 'signature' && signatureImage && !filledFields[f.field_id]?.filled) {
      autoFills[f.field_id] = { 
        type: 'signature', 
        data: signatureImage, 
        position: f.position, 
        page: f.page || 1, 
        filled: true 
      };
    }
    if (f.type === 'name' && signerName && !filledFields[f.field_id]?.filled) {
      autoFills[f.field_id] = { 
        type: 'name', 
        data: signerName, 
        position: f.position, 
        page: f.page || 1, 
        filled: true 
      };
    }
  });

  if (Object.keys(autoFills).length) {
    setFilledFields(prev => ({ ...prev, ...autoFills }));
  }
}, [fields, filledFields, signDateTime, signatureImage, signerName, setFilledFields]);

  const filledCount = Object.values(filledFields).filter(v => v.filled).length;

  const fieldColors = {
    signature: 'border-blue-500/40 bg-blue-500/5',
    name: 'border-emerald-500/40 bg-emerald-500/5',
    date: 'border-amber-500/40 bg-amber-500/5',
    text: 'border-purple-500/40 bg-purple-500/5',
    checkbox: 'border-pink-500/40 bg-pink-500/5',
  };

  const fieldLabels = {
    signature: 'Signature',
    name: 'Full Name',
    date: 'Date',
    text: 'Text',
    checkbox: 'Checkbox',
  };

  return (
    <div className="space-y-4" data-testid="step-fields">
      <div className="flex items-center justify-between">
        <p className="text-white/50 text-sm">Fill in the required fields below.</p>
        <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-sm">{filledCount}/{fields.length} filled</span>
      </div>

      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {fields.map(field => {
          const filled = filledFields[field.field_id];
          const color = fieldColors[field.type] || 'border-white/10 bg-white/5';
          return (
            <div key={field.field_id} className={`border rounded-sm p-3 transition-all ${color}`} data-testid={`field-card-${field.field_id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/70 text-xs font-medium">{field.label || fieldLabels[field.type]}</span>
                {filled?.filled && <Check className="w-3.5 h-3.5 text-green-400" />}
              </div>

              {field.type === 'signature' && (
                filled?.filled ? (
                  <div className="flex items-center gap-2">
                    <img src={filled.data} alt="Signature" className="h-8 bg-white rounded-sm px-2 object-contain" />
                    <span className="text-green-400 text-xs">Applied</span>
                  </div>
                ) : (
                  <p className="text-white/30 text-xs">Signature will be auto-applied from Step 1</p>
                )
              )}

              {field.type === 'name' && (
                <Input value={filled?.data || ''} onChange={(e) => setFilledFields(prev => ({
                  ...prev, [field.field_id]: { type: 'name', data: e.target.value, position: field.position, page: field.page || 1, filled: !!e.target.value.trim() }
                }))} placeholder="Full name" className="bg-black/30 border-white/10 text-white h-8 text-sm" data-testid={`field-name-${field.field_id}`} />
              )}

              {field.type === 'date' && (
                <p className="text-white text-sm font-mono">{filled?.data || signDateTime}</p>
              )}

              {field.type === 'text' && (
                <Input value={filled?.data || ''} onChange={(e) => setFilledFields(prev => ({
                  ...prev, [field.field_id]: { type: 'text', data: e.target.value, label: field.label, position: field.position, page: field.page || 1, formatting: field.formatting, filled: !!e.target.value.trim() }
                }))} placeholder={field.label || 'Enter text'} className="bg-black/30 border-white/10 text-white h-8 text-sm" data-testid={`field-text-${field.field_id}`} />
              )}

              {field.type === 'checkbox' && (
                <div className="space-y-1">
                  {field.question && (
                    <p className="text-white/80 text-sm">{field.question}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox checked={!!filled?.data} onCheckedChange={(checked) => setFilledFields(prev => ({
                      ...prev, [field.field_id]: { type: 'checkbox', data: checked, label: field.label, position: field.position, page: field.page || 1, filled: true }
                    }))} data-testid={`field-check-${field.field_id}`} />
                    <span className="text-white/70 text-sm">{field.label || 'Check to confirm'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================================
   STEP 3 — Review Document & Confirm
   Shows document with ALL filled fields rendered at their exact template positions
   ============================================================ */
const StepReview = ({ documentContent, documentType, documentName, signatureImage, signerName, existingSignatures, filledFields, fields }) => {
  const containerRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const signDateTime = formatSignDateTime();

  usePinchZoom(containerRef, zoom, setZoom);

  useEffect(() => {
    if (documentContent && documentType === 'application/pdf') {
      try {
        const binary = atob(documentContent);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        return () => URL.revokeObjectURL(url);
      } catch { setPdfUrl(null); }
    }
  }, [documentContent, documentType]);

  const renderDocContent = () => {
    if (documentType === 'application/pdf' && pdfUrl) {
      return (
        <Document file={pdfUrl} onLoadSuccess={({ numPages: np }) => setNumPages(np)}
          loading={<div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>}
          error={<div className="text-red-500 text-center p-4">Failed to load PDF</div>}>
          <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false}
            width={containerRef.current?.getBoundingClientRect().width || 600} />
        </Document>
      );
    }
    if (documentType?.startsWith('image/') && documentContent) {
      return <img src={`data:${documentType};base64,${documentContent}`} alt={documentName} className="w-full h-auto" />;
    }
    if (documentContent) {
      try { return <pre className="text-gray-800 text-sm p-4 whitespace-pre-wrap font-mono">{atob(documentContent)}</pre>; } catch {}
    }
    return <div className="flex items-center justify-center h-48 text-white/30">No preview available</div>;
  };

  const hasTemplateFields = fields.length > 0;

  // For free-sign (no template), place signature at bottom-right of the document
  const freeSigPos = { x: 55, y: 78 };
  const freeNamePos = { x: 55, y: 86 };

  return (
    <div className="space-y-3" data-testid="step-review">
      <p className="text-white/50 text-sm">Review the document with your fields placed. Tap "Sign Document" to confirm.</p>

      {documentType === 'application/pdf' && (
        <div className="flex items-center justify-center gap-2 py-1">
          {numPages > 1 && (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} className="text-white/70 h-7"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-white/50 text-xs">Page {currentPage}/{numPages}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages} className="text-white/70 h-7"><ChevronRight className="w-4 h-4" /></Button>
              <span className="text-white/10">|</span>
            </>
          )}
          <ZoomBar zoom={zoom} onZoomIn={() => setZoom(z => Math.min(3, z + 0.25))} onZoomOut={() => setZoom(z => Math.max(0.5, z - 0.25))} onReset={() => setZoom(1)} />
        </div>
      )}

      <div ref={containerRef} className="border border-white/20 rounded-sm bg-white overflow-auto" style={{ maxHeight: '55vh' }} data-testid="review-document-area">
        <div className="relative" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${100 / zoom}%` }}>
          {renderDocContent()}

          {/* Existing signatures from other signers */}
          {existingSignatures?.map((sig, i) => (
            <div key={sig.signature_id || i} className="absolute pointer-events-none" style={{ left: `${sig.position?.x || 60}%`, top: `${sig.position?.y || (75 + i * 10)}%`, width: '18%' }}>
              <img src={sig.signature_data} alt={`Signed by ${sig.signer_name}`} className="w-full opacity-70" />
              <p className="text-[6px] text-gray-400 text-center">{sig.signer_name} - {sig.signed_at_display || ''}</p>
            </div>
          ))}

          {/* Render ALL template fields at their exact positions */}
          {hasTemplateFields ? (
            <>
              {fields.filter(f => f.page === currentPage || !f.page).map(field => {
                const filled = filledFields[field.field_id];
                const fw = field.width || (field.type === 'checkbox' ? 8 : field.type === 'signature' ? 20 : 16);
                const fh = field.height || (field.type === 'signature' ? 6 : 4);
                const isFilled = filled?.filled;

                const borderColor = field.type === 'signature' ? 'border-blue-500' : field.type === 'name' ? 'border-emerald-500' : field.type === 'date' ? 'border-amber-500' : field.type === 'text' ? 'border-purple-500' : 'border-pink-500';
                const bgColor = isFilled ? 'bg-white/95' : 'bg-red-50/80';

                return (
                  <div key={field.field_id} className={`absolute z-10 pointer-events-none border ${isFilled ? borderColor : 'border-red-400'} ${bgColor} rounded overflow-hidden`}
                    style={{ left: `${field.position.x}%`, top: `${field.position.y}%`, width: `${fw}%`, minHeight: `${fh}%` }}
                    data-testid={`review-field-${field.field_id}`}>
                    <div className="p-0.5 h-full flex items-center">
                      {field.type === 'signature' && isFilled ? (
                        <img src={filled.data} alt="Signature" className="h-full w-full object-contain" />
                      ) : field.type === 'signature' ? (
                        <p className="text-red-400 text-[8px] w-full text-center">Missing signature</p>
                      ) : field.type === 'name' && isFilled ? (
                        <p className="text-gray-800 text-[10px] font-semibold px-1">{filled.data}</p>
                      ) : field.type === 'date' && isFilled ? (
                        <p className="text-gray-600 text-[9px] font-mono px-1">{filled.data}</p>
                      ) : field.type === 'text' && isFilled ? (
                        <p className="text-gray-800 text-[9px] px-1">{filled.data}</p>
                      ) : field.type === 'checkbox' ? (
                        <p className="text-gray-800 text-[9px] px-1">{filled?.data ? '[X]' : '[ ]'} {field.label}</p>
                      ) : (
                        <p className="text-red-400 text-[8px] px-1">{field.label || field.type}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {/* Free-sign mode: show signature + name at default positions */}
              {signatureImage && (
                <div className="absolute pointer-events-none z-10" style={{ left: `${freeSigPos.x}%`, top: `${freeSigPos.y}%`, width: '20%' }} data-testid="review-signature-overlay">
                  <div className="border-2 border-blue-500/60 rounded bg-white/90 p-1">
                    <img src={signatureImage} alt="Your signature" className="w-full" />
                  </div>
                  <p className="text-[6px] text-gray-500 text-center mt-0.5">{signDateTime}</p>
                </div>
              )}
              {signerName && (
                <div className="absolute pointer-events-none z-10" style={{ left: `${freeNamePos.x}%`, top: `${freeNamePos.y}%` }}>
                  <p className="text-[9px] text-gray-800 font-semibold">{signerName}</p>
                  <p className="text-[7px] text-gray-500">{signDateTime}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN — DocumentSignerDialog (3-step wizard)
   ============================================================ */
const DocumentSignerDialog = ({ open, onOpenChange, documentContent, documentType, documentName, onSign, existingSignatures = [], signatureFields = [], currentUserId = null }) => {
  const [step, setStep] = useState(1);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [filledFields, setFilledFields] = useState({});
  const [signing, setSigning] = useState(false);

  const userFields = signatureFields.filter(f => !f.assigned_to || f.assigned_to === currentUserId);
  const hasFields = userFields.length > 0;
  const totalSteps = hasFields ? 3 : 2; // No fields step if no template

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSignatureImage(null);
      setSignerName('');
      setFilledFields({});
      setSigning(false);
    }
  }, [open]);

  const canGoNext = () => {
    if (step === 1) return !!signatureImage;
    if (step === 2 && hasFields) return true; // fields are optional to progress
    return true;
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSign = () => {
    if (!signatureImage) return;
    setSigning(true);

    const signDateTime = formatSignDateTime();
    const sigField = userFields.find(f => f.type === 'signature');
    const position = sigField?.position || { x: 55, y: 78 };
    const nameField = userFields.find(f => f.type === 'name');
    const namePosition = nameField?.position || (signerName ? { x: position.x, y: position.y + 8 } : null);

    // Build field_values for guided mode
    const fieldValues = {};
    if (hasFields) {
      Object.entries(filledFields).forEach(([fid, fdata]) => {
        fieldValues[fid] = fdata;
      });
    }

    // Call onSign with 3 args: signature_data, position, metadata
    onSign(
      signatureImage,
      position,
      {
        signer_typed_name: signerName || null,
        signed_at_display: signDateTime,
        name_position: namePosition,
        sig_scale: 1,
        name_scale: 1,
        field_values: Object.keys(fieldValues).length > 0 ? fieldValues : null,
      }
    );

    setSigning(false);
    onOpenChange(false);
  };

  const stepLabels = hasFields
    ? ['Signature', 'Fields', 'Review']
    : ['Signature', 'Review'];

  // Determine which content step to show
  const getReviewStep = () => hasFields ? 3 : 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[95vh] overflow-hidden flex flex-col p-0" data-testid="document-signer-dialog">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-base">
              <PenTool className="w-5 h-5 text-primary" /> Sign: {documentName}
            </DialogTitle>
          </DialogHeader>
          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-3">
            {stepLabels.map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <div key={label} className="flex items-center gap-2 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${isActive ? 'bg-primary text-black' : isDone ? 'bg-primary/30 text-primary' : 'bg-white/10 text-white/30'}`}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : stepNum}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-white' : 'text-white/30'}`}>{label}</span>
                  {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${isDone ? 'bg-primary/40' : 'bg-white/10'}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === 1 && (
            <StepSignature signatureImage={signatureImage} setSignatureImage={setSignatureImage} signerName={signerName} setSignerName={setSignerName} />
          )}
          {step === 2 && hasFields && (
            <StepFields fields={userFields} filledFields={filledFields} setFilledFields={setFilledFields} signatureImage={signatureImage} signerName={signerName} />
          )}
          {step === getReviewStep() && (
            <StepReview documentContent={documentContent} documentType={documentType} documentName={documentName}
              signatureImage={signatureImage} signerName={signerName} existingSignatures={existingSignatures}
              filledFields={filledFields} fields={userFields} />
          )}
        </div>

        {/* Footer nav */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3">
          {step > 1 ? (
            <Button type="button" variant="outline" onClick={handleBack} className="border-white/10 text-white/70 hover:bg-white/5" data-testid="step-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-white/10 text-white/70 hover:bg-white/5" data-testid="step-cancel-btn">
              Cancel
            </Button>
          )}

          {step < totalSteps ? (
            <Button type="button" onClick={handleNext} disabled={!canGoNext()} className="bg-primary text-black hover:bg-primary/90" data-testid="step-next-btn">
              Next <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button type="button" onClick={handleSign} disabled={!signatureImage || signing} className="bg-primary text-black hover:bg-primary/90 font-semibold" data-testid="final-sign-btn">
              {signing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Sign Document
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================
   Simple Signature Dialog (for registration inline signing)
   ============================================================ */
const SignatureDialog = ({ open, onOpenChange, onSign, title = "Sign Document", description = "Draw your signature below" }) => {
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');

  const handleClear = () => { const pad = canvasRef.current?._pad?.current; if (pad) pad.clear(); setHasDrawn(false); setTypedName(''); };
  const handleSign = () => {
    if (mode === 'draw') { const pad = canvasRef.current?._pad?.current; if (pad && !pad.isEmpty()) { onSign(pad.toDataURL('image/png')); onOpenChange(false); handleClear(); } }
    else if (typedName.trim()) { onSign(generateTypedSignature(typedName)); onOpenChange(false); handleClear(); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClear(); onOpenChange(v); }}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><PenTool className="w-5 h-5 text-primary" />{title}</DialogTitle></DialogHeader>
        <p className="text-white/60 text-sm">{description}</p>
        <div className="mt-3 space-y-4">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === 'draw' ? 'default' : 'outline'} onClick={() => setMode('draw')}
              className={mode === 'draw' ? 'bg-primary text-black' : 'border-white/10 text-white hover:bg-white/5'} data-testid="sig-mode-draw"><PenTool className="w-3 h-3 mr-1" /> Draw</Button>
            <Button type="button" size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')}
              className={mode === 'type' ? 'bg-primary text-black' : 'border-white/10 text-white hover:bg-white/5'} data-testid="sig-mode-type"><Type className="w-3 h-3 mr-1" /> Type</Button>
          </div>
          {mode === 'draw' ? <SignatureCanvas canvasRef={canvasRef} onDrawEnd={() => setHasDrawn(true)} height={180} /> : (
            <div className="space-y-3">
              <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name" className="bg-black/50 border-white/10 text-white h-11" data-testid="sig-type-input" />
              {typedName.trim() && <div className="border border-white/20 rounded-sm bg-white p-4 text-center"><span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '28px', color: '#1a1a2e' }}>{typedName}</span></div>}
            </div>
          )}
          <div className="flex gap-3">
            <Button onClick={handleClear} variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5" data-testid="signature-clear-btn"><Eraser className="w-4 h-4 mr-2" /> Clear</Button>
            <Button onClick={handleSign} disabled={mode === 'draw' ? !hasDrawn : !typedName.trim()} className="flex-1 bg-primary text-black hover:bg-primary/90" data-testid="signature-confirm-btn"><Check className="w-4 h-4 mr-2" /> Confirm</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================
   Inline components (registration page)
   ============================================================ */
const InlineSignaturePad = ({ onSign, onClear, signatureData }) => {
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');

  const handleConfirm = () => {
    if (mode === 'draw') { const pad = canvasRef.current?._pad?.current; if (pad && !pad.isEmpty()) onSign(pad.toDataURL('image/png')); }
    else if (typedName.trim()) onSign(generateTypedSignature(typedName));
  };
  const handleClear = () => { const pad = canvasRef.current?._pad?.current; if (pad) pad.clear(); setHasDrawn(false); setTypedName(''); if (onClear) onClear(); };

  if (signatureData) return (
    <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-sm border border-primary/20">
      <img src={signatureData} alt="Your signature" className="h-6 object-contain bg-white rounded-sm px-2" />
      <span className="text-primary text-xs">Signed</span>
      <button type="button" onClick={handleClear} className="ml-auto text-white/40 hover:text-white text-xs">Re-sign</button>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        <Button type="button" size="sm" variant={mode === 'draw' ? 'default' : 'outline'} onClick={() => setMode('draw')}
          className={`h-6 text-xs px-2 ${mode === 'draw' ? 'bg-primary text-black' : 'border-white/10 text-white hover:bg-white/5'}`}><PenTool className="w-2.5 h-2.5 mr-1" /> Draw</Button>
        <Button type="button" size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')}
          className={`h-6 text-xs px-2 ${mode === 'type' ? 'bg-primary text-black' : 'border-white/10 text-white hover:bg-white/5'}`}><Type className="w-2.5 h-2.5 mr-1" /> Type</Button>
      </div>
      {mode === 'draw' ? <SignatureCanvas canvasRef={canvasRef} onDrawEnd={() => setHasDrawn(true)} height={100} /> : (
        <div className="space-y-1">
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name" className="bg-black/50 border-white/10 text-white h-8 text-xs" data-testid="inline-sig-type-input" />
          {typedName.trim() && <div className="border border-white/20 rounded-sm bg-white p-2 text-center"><span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '20px', color: '#1a1a2e' }}>{typedName}</span></div>}
        </div>
      )}
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" className="flex-1 border-white/10 text-white h-7 text-xs" onClick={handleClear}>Clear</Button>
        <Button type="button" size="sm" className="flex-1 bg-primary text-black h-7 text-xs" disabled={mode === 'draw' ? !hasDrawn : !typedName.trim()} onClick={handleConfirm} data-testid="inline-sig-confirm"><Check className="w-3 h-3 mr-1" /> Confirm</Button>
      </div>
    </div>
  );
};

const InlinePdfViewer = ({ base64Content, maxHeight = 250 }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    if (base64Content) {
      const binary = atob(base64Content); const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' }); const url = URL.createObjectURL(blob); setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [base64Content]);

  if (!pdfUrl) return null;
  return (
    <div>
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-1 bg-black/30">
          <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} className="text-white/60 h-6 px-1"><ChevronLeft className="w-3 h-3" /></Button>
          <span className="text-white/40 text-[10px]">{currentPage}/{numPages}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages} className="text-white/60 h-6 px-1"><ChevronRight className="w-3 h-3" /></Button>
        </div>
      )}
      <div ref={containerRef} className="bg-white overflow-auto" style={{ maxHeight: `${maxHeight}px` }}>
        <Document file={pdfUrl} onLoadSuccess={({ numPages: np }) => setNumPages(np)}
          loading={<div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>}
          error={<div className="text-red-500 text-center p-4 text-xs">Failed to load PDF</div>}>
          <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false} width={containerRef.current?.getBoundingClientRect().width || 400} />
        </Document>
      </div>
    </div>
  );
};

const SignatureDisplay = ({ signatures = [] }) => {
  if (!signatures.length) return null;
  return (
    <div className="space-y-2" data-testid="signature-display">
      {signatures.map((sig, i) => (
        <div key={sig.signature_id || i} className="flex items-center gap-3 p-2 bg-white/5 rounded-sm border border-white/10">
          <img src={sig.signature_data} alt="Signature" className="h-8 object-contain bg-white rounded-sm px-2" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate">{sig.signer_name}</p>
            <p className="text-white/40 text-xs">{new Date(sig.signed_at).toLocaleDateString()}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export { SignatureDialog, DocumentSignerDialog, SignatureDisplay, InlineSignaturePad, InlinePdfViewer };
